import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getCachedBlob, putBlobInCache } from "@/lib/appCache";
import { resolveRecipeImageFull } from "@/lib/resolveRecipeImage";
import RecipeFallbackCover from "@/components/RecipeFallbackCover";

const STOCK_PHOTO_MESSAGES: Record<string, string> = {
  en: "Photo coming soon",
  es: "Foto próximamente",
  de: "Foto kommt bald",
};

function isStockPhotoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  return /unsplash\.com|pexels\.com|images\.pexels/i.test(url);
}

interface CachedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  cacheKey?: string;
  /** Optional category for premium fallback when image fails to load */
  fallbackCategory?: string | null;
  /** Optional image group for real-image fallback before category placeholder */
  imageGroup?: string | null;
  /** Optional title shown on fallback cover for visual differentiation */
  fallbackTitle?: string;
  /** Serve a small thumbnail (400px, q60) instead of the full image */
  thumbnail?: boolean;
}

const CachedImage = ({ src, cacheKey, className, alt, fallbackCategory, imageGroup, fallbackTitle, thumbnail = false, ...props }: CachedImageProps) => {
  const { i18n } = useTranslation();
  const resolved = resolveRecipeImageFull(src, imageGroup, fallbackCategory, thumbnail);
  const resolvedSrc = resolved.url;
  const isStockPhoto = isStockPhotoUrl(resolvedSrc);
  const stockPhotoMessage = STOCK_PHOTO_MESSAGES[i18n.language] || STOCK_PHOTO_MESSAGES.en;
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  // A foto entra com um fade quando termina de carregar (.foto-entra).
  const [pronta, setPronta] = useState(false);
  const [inView, setInView] = useState(false);
  const revokeRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const key = cacheKey
    ? `${cacheKey}${thumbnail ? ":thumb" : ""}`
    : resolvedSrc || `${fallbackCategory || "fallback"}:${imageGroup || "none"}`;

  // IntersectionObserver — only start loading when element is near viewport
  useEffect(() => {
    const el = containerRef.current || imgRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" } // preload 200px before entering viewport
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || !resolvedSrc) {
      if (!resolvedSrc) setHasError(true);
      return;
    }

    let cancelled = false;
    setHasError(false);
    setObjectUrl(null);

    (async () => {
      const cached = await getCachedBlob(key);
      if (cached && !cancelled) {
        revokeRef.current = cached;
        setObjectUrl(cached);
        return;
      }

      try {
        const res = await fetch(resolvedSrc);
        if (!res.ok || cancelled) {
          if (!cancelled) setHasError(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        await putBlobInCache(key, blob);
        const url = URL.createObjectURL(blob);
        revokeRef.current = url;
        setObjectUrl(url);
      } catch {
        if (!cancelled) setHasError(true);
      }
    })();

    return () => {
      cancelled = true;
      if (revokeRef.current) {
        URL.revokeObjectURL(revokeRef.current);
        revokeRef.current = null;
      }
    };
  }, [inView, resolvedSrc, key]);

  if (hasError || !resolvedSrc) {
    // Um caminho só de fallback: a capa desenhada. O segundo caminho existia
    // porque sem título não dava pra montar a capa antiga — a de agora não
    // depende do título, então não há o que ramificar.
    return (
      <RecipeFallbackCover
        title={fallbackTitle || (typeof alt === "string" ? alt : "")}
        category={fallbackCategory}
        className={className || ""}
      />
    );
  }

  // Not yet in view — show shimmer placeholder matching layout
  if (!inView) {
    return (
      <div ref={containerRef} className={`${className || ""} skeleton-base`} />
    );
  }

  if (isStockPhoto) {
    return (
      <div className={`relative overflow-hidden ${className || ""}`}>
        <img
          {...props}
          ref={imgRef}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ ...props.style, filter: "grayscale(100%) blur(8px)" }}
          alt={alt}
          src={objectUrl || resolvedSrc}
          onError={() => setHasError(true)}
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/55 px-3 text-center text-sm font-medium text-white pointer-events-none">
          {stockPhotoMessage}
        </div>
      </div>
    );
  }

  return (
    <img
      {...props}
      ref={imgRef}
      className={`${className || ""} foto-entra`}
      data-pronta={pronta ? "1" : "0"}
      alt={alt}
      src={objectUrl || resolvedSrc}
      onLoad={(e) => {
        setPronta(true);
        props.onLoad?.(e);
      }}
      onError={() => setHasError(true)}
    />
  );
};

export default CachedImage;
